// DeliverySizeSplit — the two presentational pieces of the berry intake size
// split (mig 20260813100000), lifted out of BlueberryDeliveries so they can be
// rendered in a test without standing up the whole board.
//
// They carry the one message the numbers alone don't: reject crates sit OUTSIDE
// the delivery's crate count. Both spell that out on screen, which is why they
// are worth testing rather than eyeballing.

import { Fragment } from 'react'
import { weighGroupNetKg, sortSizeBreakdown, type SizeBreakdownRow } from '@/lib/berryWeighing'


// The per-size chips under a board card. Rejects are chipped apart in amber and
// captioned, because they sit OUTSIDE the crate count printed above them —
// without saying so the two numbers read as a discrepancy.
export function SizeChips({ rows, order, rejects, cratesReceived }: {
  rows:           SizeBreakdownRow[]
  order:          string[]
  rejects:        number
  cratesReceived: number
}) {
  return (
    <p className="mt-1 flex flex-wrap gap-1">
      {sortSizeBreakdown(rows, order).map(s => (
        <span key={`${s.size_class ?? 'reject'}-${String(s.is_reject)}`}
          className={s.is_reject
            ? 'rounded px-1.5 py-0.5 text-[10px] bg-amber-900/40 text-amber-300 border border-amber-800/50 print:bg-white print:text-black print:border-gray-400'
            : 'rounded px-1.5 py-0.5 text-[10px] bg-surface text-fg-secondary border border-line print:bg-white print:text-black print:border-gray-300'}>
          {s.crates} {s.is_reject ? 'reject' : s.size_class}
          {Number(s.net_kg) > 0 && <span className="opacity-60"> · {Number(s.net_kg).toFixed(1)} kg</span>}
        </span>
      ))}
      {rejects > 0 && (
        <span className="text-[10px] text-fg-muted self-center print:text-gray-700">
          rejects not counted in the {cratesReceived}
        </span>
      )}
    </p>
  )
}

// One row of the intake split: a size (or the reject bucket), its crates, an
// optional gross, and the net that falls out of the two.
export function SizeSplitRow({ label, isReject, value, tareKg, onChange }: {
  label:    string
  isReject: boolean
  value:    { crates: string; grossKg: string }
  tareKg:   number
  onChange: (patch: Partial<{ crates: string; grossKg: string }>) => void
}) {
  const crates = parseInt(value.crates, 10) || 0
  const gross  = parseFloat(value.grossKg)  || 0
  const net    = gross > 0 ? weighGroupNetKg(gross, crates, tareKg) : null
  return (
    <Fragment>
      <span className={isReject ? 'text-xs text-amber-300 truncate' : 'text-xs text-gray-200 light:text-fg truncate'}>
        {label}
        {isReject && <span className="block text-[9px] text-gray-500 leading-tight">not counted in the delivery</span>}
      </span>
      <input type="number" inputMode="numeric" min={0} className="field" placeholder="0"
        aria-label={`${label} crates`} value={value.crates}
        onChange={e => onChange({ crates: e.target.value })} />
      <input type="number" inputMode="decimal" min={0} step="0.1" className="field" placeholder="—"
        aria-label={`${label} gross weight in kg`} value={value.grossKg}
        onChange={e => onChange({ grossKg: e.target.value })} />
      <span className="text-xs text-fg-secondary tabular-nums text-right">
        {net != null ? net.toFixed(1) : '—'}
      </span>
    </Fragment>
  )
}
